import { Button, Grid, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import { SupportSheetStatus } from '../SupportSheet/models/SupportSheetModel';
import { UserGroupEnum } from '../UserManagement/UserGroupEnum';
import FixtureApi, { DeleteParam, SplitFixtureParam } from './api/FixtureApi';
import CommissionItemModel from './components/FixtureCommissionDetail/models/CommissionItemModel';
import { setSelectedFixtures, setShowQuickSummaryModal } from './components/FixtureQuickSummary/reducers/FixtureQuickSummaryReducer';
import FixtureModel from './models/FixtureModel';
import { FixtureInputSelector, resetFixtureInput, setHideEva, setHideInvoice, setIsEditFixture, setSelectedFixtureId, setShowInputPanel, setTriggerFocusChartererInput } from './reducers/FixtureInputReducer';
import { setFixtureInputId, setShowInvoiceInputPanel } from './reducers/FixtureInvoiceInputReducer';
import { FixtureSearchSelector, setCheckedFixtures, setDatatableSelectedFixtureIndex, setOrderBy, setOrderDirection, setTriggerFixtureSearch } from './reducers/FixtureSearchReducer';
import { setShowInputBrokerAccountPanel } from '../BrokerAccount/reducers/BrokerAccountSearchReducer';
import Api from '../../app/apiRequest';
import { isInRole } from '../MenuBar/MenuBar';

const FixtureTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();

    let fixtureData = useSelector(FixtureSearchSelector);
    const fixtureInputViewData = useSelector(FixtureInputSelector);
    const userProfile = useSelector(userProfileSelector);

    const divRef = React.createRef<HTMLDivElement>();

    const editInvoice = (rowData: FixtureModel) => {
        dispatch(setFixtureInputId(rowData.id));
        dispatch(setShowInvoiceInputPanel(true));
    }

    const getColumns = () => {
        let generalColumns = [
            {
                Field: "",
                Title: "No",
                AllowSort: false,
                CellHeaderClassName: "fixture-col-sticky fixture-col-header fixture-col2",
                CellClassName: "fixture-col-sticky fixture-col fixture-col2",
                Render: (rowData: FixtureModel, index: number) => {
                    return <>{index + 1}</>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("ref"),
                Title: "Ref",
                AllowSort: true,
                CellHeaderClassName: "fixture-col-sticky fixture-col-header fixture-col3",
                CellClassName: "fixture-col-sticky fixture-col  fixture-col3",
                Render: (rowData: FixtureModel) => {
                    return <>{rowData.ref}</>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("cpDate"),
                Title: "CP Date",
                AllowSort: true,
                CellHeaderClassName: "fixture-col-sticky fixture-col-header fixture-col4",
                CellClassName: "fixture-col-sticky fixture-col fixture-col4",
                Render: (rowData: FixtureModel) => {
                    return <span className='fixture-same-line'>{Utility.DateFormat.formatDateString(rowData.cpDate)}</span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("tonnageVesselName"),
                Title: "Vessel Name",
                AllowSort: true,
                CellHeaderClassName: "fixture-col-sticky fixture-col-header fixture-col5",
                CellClassName: "fixture-col-sticky fixture-col fixture-col5",
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("ownerCompanyName"),
                Title: "Owner",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("ownerBrokerName"),
                Title: "Owner Brkr",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("chartererCompanyName"),
                Title: "Charterer",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("chartererBrokerName"),
                Title: "Chtr Brkr",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("quantityOrDuration"),
                Title: "Qtty or Dur",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("rate"),
                Title: "Rate",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("estdComm"),
                Title: "Estd Comm",
                AllowSort: true,
                Render: (rowData: FixtureModel) => (
                    <>
                        {rowData.estdComm ? Utility.NumberFormat.formatUSD(rowData.estdComm) : ""}
                    </>
                )
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("collectFromCompanyName"),
                Title: "Collect From",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
        ];

        const adminAndFinanceColumns = isInRole(userProfile.userGroup, [UserGroupEnum.Administrator, UserGroupEnum.Finance])?[
            {
                Field: Utility.propertyOf<FixtureModel>("privateCommRemark"),
                Title: "CBR",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    return <>{!rowData.privateCommRemark ? "" : rowData.privateCommRemark.length <= 50 ? rowData.privateCommRemark : rowData.privateCommRemark.substring(0, 15)}</>;
                }
            } as DatatableColumnModel<FixtureModel>
        ]: [];

        generalColumns =[...generalColumns, ...adminAndFinanceColumns,
        ...[
            {
                Field: Utility.propertyOf<FixtureModel>("supportName"),
                Title: "Support",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("status"),
                Title: "Status",
                AllowSort: true,
            } as DatatableColumnModel<FixtureModel>,
        ]];


        const invoiceColumns = [
            {
                Field: Utility.propertyOf<FixtureModel>("invoiceNo"),
                Title: "Inv No",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => {
                    return <span>
                        {rowData.invoiceNo}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("issDate"),
                Title: "Inv Date",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => {
                    return <span className='fixture-same-line'>
                        {Utility.DateFormat.formatDateString(rowData.issDate)}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("amount"),
                Title: "Inv Amount",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => (
                    <>
                        {rowData.amount ? Utility.NumberFormat.formatUSD(rowData.amount) : ""}
                    </>
                )
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("bankAccount"),
                Title: "Bank Account",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => {
                    return <span>
                        {rowData.bankAccount}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("bankReceived"),
                Title: "Bank Received",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => (
                    <>
                        {rowData.bankReceived ? Utility.NumberFormat.formatUSD(rowData.bankReceived) : ""}
                    </>
                )
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("rcvDate"),
                Title: "Received Date",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => {
                    return <span className='fixture-same-line'>{Utility.DateFormat.formatDateString(rowData.rcvDate)}</span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("commCalculated"),
                Title: "Comm Calculated",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => (
                    <>
                        {rowData.bankReceived && rowData.bankReceived > 0 && Utility.NumberFormat.formatUSD(rowData.bankReceived - rowData.privateCommSettled)}
                    </>
                )
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("privateCommSettled"),
                Title: "CBR Coms Settled",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => {
                    return <span>
                        {Utility.NumberFormat.formatUSD(rowData.privateCommSettled)}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("settledDate"),
                Title: "Settled Date",
                AllowSort: true,
                onCellClick: (rowData: FixtureModel) => {
                    editInvoice(rowData);
                },
                Render: (rowData: FixtureModel) => {
                    return <span>
                        {Utility.DateFormat.formatDateString(rowData.settledDate)}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
        ];
        const evaColumns = [
            {
                Field: Utility.propertyOf<FixtureModel>("initEvaVolume"),
                Title: "Eva Volume",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    return <span>
                        {rowData.initEvaVolume}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("initEvaComplex"),
                Title: "Eva Complex",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    return <span>
                        {rowData.initEvaComplex}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("evaSupport"),
                Title: "Eva Support",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    return <span className='fixture-same-line'>
                        {rowData.status === SupportSheetStatus.CloseFile && (!rowData.evaSupport || rowData.evaSupport == 0) ? "Need Support Eva" : rowData.evaSupport}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("evaManager"),
                Title: "Eva Manager",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    return <span className='fixture-same-line'>
                        {rowData.status === SupportSheetStatus.CloseFile && rowData.evaSupport && rowData.evaSupport > 0 && !rowData.evaManager ? "Need Manager Eva" : rowData.evaManager}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("evaBroker"),
                Title: "Eva Broker",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    return <span className='fixture-same-line'>
                        {rowData.status === SupportSheetStatus.CloseFile && rowData.evaManager && rowData.evaManager > 0 && !rowData.evaBroker ? "Need Broker Eva" : rowData.evaBroker}
                    </span>;
                }
            } as DatatableColumnModel<FixtureModel>,
        ];
        const lastColumns = [
            {
                Field: Utility.propertyOf<FixtureModel>("commRateDetail"),
                Title: "Comm Rate Detail",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    let commDetails = JSON.parse(rowData.commRateDetail) as CommissionItemModel[];

                    let commRateDetail = "";
                    _.each(commDetails, (commItem) => {
                        commRateDetail += commItem.brokerName + commItem.percent + " ";
                    })


                    return <>{commRateDetail}</>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: Utility.propertyOf<FixtureModel>("remarks"),
                Title: "Remarks",
                AllowSort: true,
                Render: (rowData: FixtureModel) => {
                    return <>{!rowData.remarks ? "" : rowData.remarks.length <= 50 ? rowData.remarks : rowData.remarks.substring(0, 15)}</>;
                }
            } as DatatableColumnModel<FixtureModel>,
            {
                Field: "",
                Title: "",
                AllowSort: true,
                Render: (rowData: FixtureModel) => (
                    <>
                        <Tooltip title="Edit">
                            <Button
                                className='table-action-button'
                                onClick={() => {
                                    dispatch(setSelectedFixtureId(rowData.id));
                                    dispatch(setShowInputPanel(true));
                                    dispatch(setIsEditFixture(true));
                                }}
                                variant='outlined'
                            >
                                <i className="fa fa-pencil" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                        <Tooltip title="View Support Sheet">
                            <Button
                                className='table-action-button'
                                onClick={() => {
                                    let url = "/support-sheet?fixtureId=" + rowData.id;

                                    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
                                    if (newWindow) newWindow.opener = null
                                }}
                                variant='outlined'
                            >
                                <i className="fa fa-file-text-o" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                        <Tooltip title="Split Fixture">
                            <Button
                                className='table-action-button'
                                onClick={() => {
                                    Swal.fire({
                                        title: 'Are you sure want split this Fixture?',
                                        icon: 'warning',
                                        showCancelButton: true,
                                        confirmButtonColor: '#3085d6',
                                        cancelButtonColor: '#d33',
                                        confirmButtonText: 'Yes',
                                        cancelButtonText: 'No'
                                    }).then((result) => {
                                        if (result.isConfirmed) {
                                            dispatch(showLoading(true));
                                            FixtureApi.SplitFixture({
                                                id: rowData.id
                                            } as SplitFixtureParam)
                                                .then((response: BaseResponse<string>) => {
                                                    if (response.success) {
                                                        Utility.Alert.Success("Split fixture successful");
                                                        dispatch(setTriggerFixtureSearch((new Date()).getTime()))
                                                    } else {
                                                        dispatch(showLoading(false));
                                                        Utility.Alert.Error(response.message);
                                                    }
                                                })
                                                .catch((error) => {
                                                    dispatch(showLoading(false));
                                                    Utility.Alert.Error(error);
                                                });
                                        }
                                    })


                                }}
                                variant='outlined'
                            >
                                <i className="fa fa-columns" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                        {isInRole(userProfile.userGroup, [UserGroupEnum.Administrator, UserGroupEnum.SupportManager]) &&
                            <Tooltip title="Delete Fixture">
                                <Button
                                    className='table-action-button'
                                    variant='outlined'
                                    color='error'
                                    onClick={() => {
                                        deleteFixture(rowData.id)
                                    }}
                                >
                                    <i className="fa fa-trash" aria-hidden="true"></i>
                                </Button>
                            </Tooltip>
                        }
                    </>
                )
            } as DatatableColumnModel<FixtureModel>,
        ];

        let columns = [...generalColumns];

        if (!fixtureInputViewData.hideInvoice) {
            columns = [...columns, ...invoiceColumns];
        }

        if (!fixtureInputViewData.hideEva) {
            columns = [...columns, ...evaColumns];
        }

        columns = [...columns, ...lastColumns];

        return columns;
    };

    const columnsForCopying = [
        {
            Field: Utility.propertyOf<FixtureModel>("ref"),
            Title: "Ref",
            AllowSort: true,
            Render: (rowData: FixtureModel) => {
                return <>{rowData.ref}</>;
            }
        } as DatatableColumnModel<FixtureModel>,
        {
            Field: Utility.propertyOf<FixtureModel>("cpDate"),
            Title: "CP Date",
            AllowSort: true,
            Render: (rowData: FixtureModel) => {
                return <span className='fixture-same-line'>{Utility.DateFormat.formatDateString(rowData.cpDate)}</span>;
            }
        } as DatatableColumnModel<FixtureModel>,
        {
            Field: Utility.propertyOf<FixtureModel>("tonnageVesselName"),
            Title: "Vessel Name",
            AllowSort: true,
        } as DatatableColumnModel<FixtureModel>,
        {
            Field: Utility.propertyOf<FixtureModel>("ownerCompanyName"),
            Title: "Owner",
            AllowSort: true,
        } as DatatableColumnModel<FixtureModel>,
        {
            Field: Utility.propertyOf<FixtureModel>("chartererCompanyName"),
            Title: "Charterer",
            AllowSort: true,
        } as DatatableColumnModel<FixtureModel>,
        {
            Field: Utility.propertyOf<FixtureModel>("status"),
            Title: "Status",
            AllowSort: true,
        } as DatatableColumnModel<FixtureModel>,
        {
            Field: Utility.propertyOf<FixtureModel>("invoiceNo"),
            Title: "Inv No",
            AllowSort: true,
            onCellClick: (rowData: FixtureModel) => {
                editInvoice(rowData);
            },
            Render: (rowData: FixtureModel) => {
                return <span>
                    {rowData.invoiceNo}
                </span>;
            }
        } as DatatableColumnModel<FixtureModel>,
        {
            Field: Utility.propertyOf<FixtureModel>("issDate"),
            Title: "Inv Date",
            AllowSort: true,
            onCellClick: (rowData: FixtureModel) => {
                editInvoice(rowData);
            },
            Render: (rowData: FixtureModel) => {
                return <span className='fixture-same-line'>
                    {Utility.DateFormat.formatDateString(rowData.issDate)}
                </span>;
            }
        } as DatatableColumnModel<FixtureModel>,
    ];

    const getTableClassName = () => {
        let className = "fixture-datatable-search-result nice-scroll fixture-same-line";

        if (!fixtureInputViewData.fixtureInputExpand) {
            className += " fixture-datatable-search-result-input-collapse";
        }

        return className;
    }

    const deleteFixture = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this Fixture? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                FixtureApi.Delete({
                    id: id
                } as DeleteParam)
                    .then((response: BaseResponse<FixtureModel[]>) => {
                        if (response.success) {
                            Utility.Alert.Success("Fixture deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerFixtureSearch(new Date().getTime()));
                        }
                    });
            }
        })
    };

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                {
                    fixtureInputViewData.hideInvoice &&
                    <Button
                        color="secondary"
                        variant='outlined'
                        className='table-search-result-utility-bar-item'
                        onClick={() => {
                            dispatch(setHideInvoice(false));
                        }}
                    >Show Invoice</Button>
                }
                {
                    !fixtureInputViewData.hideInvoice &&
                    <Button
                        color="error"
                        variant='outlined'
                        className='table-search-result-utility-bar-item'
                        onClick={() => {
                            dispatch(setHideInvoice(true));
                        }}
                    >Hide Invoice</Button>
                }
            </Grid>
            <Grid>
                {
                    fixtureInputViewData.hideEva &&
                    <Button
                        color="secondary"
                        variant='outlined'
                        className='table-search-result-utility-bar-item'
                        onClick={() => {
                            dispatch(setHideEva(false));
                        }}
                    >Show Evaluation</Button>
                }
                {
                    !fixtureInputViewData.hideEva &&
                    <Button
                        color="error"
                        variant='outlined'
                        className='table-search-result-utility-bar-item'
                        onClick={() => {
                            dispatch(setHideEva(true));
                        }}
                    >Hide Evaluation</Button>
                }
            </Grid>
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        dispatch(resetFixtureInput())
                        dispatch(setSelectedFixtureId(""));
                        dispatch(setTriggerFocusChartererInput((new Date()).getTime()));
                        dispatch(setShowInputPanel(true));
                        dispatch(setIsEditFixture(false));
                    }}
                >Handover</Button>
            </Grid>
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let div = document.getElementById("tonnage-table-copy");
                        if (!div) {
                            return;
                        }

                        div.className = "tonnage-table-copy-show";

                        let selection = window.getSelection();
                        if (selection) {
                            let range = document.createRange();
                            range.selectNodeContents(divRef.current as Node);
                            selection.removeAllRanges();
                            selection.addRange(range);
                            document.execCommand("copy");
                            Utility.Alert.Success("Copied to clipboard");
                        }

                        div.className = "tonnage-table-copy-hide";
                    }}
                >Copy Fixture List</Button>
            </Grid>
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        if (fixtureData.checkedFixtures.length === 0) {
                            Utility.Alert.Error("Please select fixtures before analyzing")
                        } else {
                            dispatch(setShowQuickSummaryModal(true));
                        }
                    }}
                >Quick Summary</Button>
            </Grid>
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        dispatch(setShowInputBrokerAccountPanel(true))
                    }}
                >Broker Account</Button>
            </Grid>
            {
                (isInRole(userProfile.userGroup, [UserGroupEnum.Administrator, UserGroupEnum.SupportManager, UserGroupEnum.Finance])) &&
                <Grid>
                    <Button
                        color="secondary"
                        variant='outlined'
                        className='table-search-result-utility-bar-item'
                        onClick={() => {
                            let url = Api.constructGetUrl("/api/Fixture/ExportExcel", fixtureData.query);

                            const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
                            if (newWindow) newWindow.opener = null
                        }}
                    >Export Excel</Button>
                </Grid>
            }

            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {fixtureData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<FixtureModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={fixtureData.searchResults}
            useItemPerPage={false}
            columns={getColumns()}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<FixtureModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerFixtureSearch((new Date()).getTime()));
            }}
            onRowSelectedChange={(Fixture: FixtureModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedFixtureId(Fixture.id))
                    dispatch(setDatatableSelectedFixtureIndex(index));
                }
            }}
            selectedRowIndex={fixtureData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={fixtureData.query.orderBy}
            orderDirection={fixtureData.query.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: FixtureModel[]) => {
                dispatch(setCheckedFixtures(selectedRows));
                dispatch(setSelectedFixtures(selectedRows))
            }}
            isDisableRowClassName="Fixture-row-fixed"
            checkBoxColumnHeaderClassName="fixture-col-sticky fixture-col-header fixture-col1"
            checkBoxColumnClassName="fixture-col-sticky fixture-col fixture-col1"
        ></Datatable>
        <div id="tonnage-table-copy"
            className='tonnage-table-copy-hide'
            contentEditable="true"
            ref={divRef}
        >
            <Datatable<FixtureModel>
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={fixtureData.checkedFixtures}
                useItemPerPage={false}
                columns={columnsForCopying}
                isDataLoaded={true}
                className={getTableClassName()}
            ></Datatable>
        </div>
    </div>
};

export default FixtureTable;